import { useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { HandbookTable } from "@/components/handbook/handbook-table";
import { HandbookFilters } from "@/components/handbook/handbook-filters";
import { handbooksQueryOptions } from "@/hooks/use-handbook";
import { usePagination } from "@/hooks/use-pagination";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import type { HandbookFilters as HandbookFiltersType } from "@/types/handbook";

export default function HandbookPage() {
  const navigate = useNavigate();
  const [filters, setFiltersState] = useState<HandbookFiltersType>({});
  const pagination = usePagination({ initialLimit: 10 });

  const setFilters = (next: HandbookFiltersType) => {
    setFiltersState(next);
    pagination.setPage(1);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">자료집 관리</h1>
          <p className="text-muted-foreground leading-normal">
            유저용 자료집(위키) 페이지를 작성하고 발행합니다.
          </p>
        </div>
        <Button onClick={() => navigate("/handbook/new")}>
          <Plus className="mr-2 h-4 w-4" />새 자료집
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>자료집 목록</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-4 md:px-6">
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<TableLoadingFallback columnCount={7} />}>
                  <HandbookFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                  />
                  <SuspenseQuery
                    {...handbooksQueryOptions(filters, {
                      page: pagination.page,
                      limit: pagination.limit,
                    })}
                  >
                    {({ data }) => (
                      <HandbookTable
                        data={data.items}
                        meta={data.meta}
                        pagination={pagination}
                      />
                    )}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>
    </div>
  );
}
